import {
    faBook,
    faBorderAll,
    faChalkboardTeacher,
    faCheckCircle,
    faChevronDown,
    faFileInvoice,
    faMobile,
    faTablet,
    faTerminal,
    faUser,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@ui';
import { AnimatedBeam } from '@ui/AnimatedComponents/AnimatedBeamPaths';
import ModulesGrid from '@ui/AnimatedComponents/ModulesGrid';
import Footer from 'components/Footer';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { forwardRef, useEffect, useRef, useState, type ReactNode } from 'react';
import LogoLoader from '~/components/LogoLoader';
import AgenticBall from '~/components/ui/AnimatedComponents/AgenticBall';
import CountUp from '~/components/ui/AnimatedComponents/CountUp';
import { ElegantShape } from '~/components/ui/AnimatedComponents/ElegantShape';
import Highlighter from '~/components/ui/AnimatedComponents/Highlighter';
import { LogoCarousel } from '~/components/ui/AnimatedComponents/LogoCarousel';
import { Chatbot } from '~/components/ui/Chatbot/Chatbot';
import { FeatureRow, PriceSection } from '~/components/ui/Layouts/Sections';
import { AiCmsShowcaseSection } from '~/components/ui/Layouts/Sections/AiCmsShowcaseSection';
import { NavbarPages } from '~/components/ui/Navbar/NavbarPages';
import useMediaQuery from '~/hooks/useMediaQuery';
import { cn } from '~/lib/cn';

const modules = [
    {
        title: 'Mitgliederverwaltung',
        description:
            'Verwalte alle Mitglieder zentral und übersichtlich. Automatisierte Abläufe, klare Strukturen und sichere Datenhaltung sorgen für eine effiziente und moderne Vereinsorganisation.',
        link: '/mitgliederverwaltung',
        icon: faUsers,
        variant: 'Digital',
        highlights: ['Zentrale Daten', 'Automatisierte Abläufe', 'Sichere Verwaltung'],
        width: 2
    },
    {
        title: 'App für Mitglieder',
        description:
            'Biete deinen Mitgliedern jederzeit Zugriff auf Buchungen, Trainingspläne und Vereinsinformationen. Die App ermöglicht einfache Nutzung und schafft mehr Transparenz und direkte Kommunikation.',
        link: '/mitgliederverwaltung',
        icon: faMobile,
        variant: 'Digital',
        highlights: ['Direkte Kommunikation', 'Einfache Nutzung', 'Mehr Transparenz']
    },
    {
        title: 'Kiosksysteme',
        description:
            'Ermögliche deinen Mitgliedern bequemen Self Service direkt vor Ort. Kiosksysteme unterstützen Check Ins, Buchungen und Informationsabrufe und machen deine Sportanlage moderner und effizienter.',
        link: '/platzbuchungen',
        icon: faTablet,
        variant: 'Hardware',
        highlights: ['Self Service', 'Schnelle Bedienung', 'Vor Ort optimiert']
    },
    {
        title: 'IOT Geräte',
        description:
            'Vernetzte Geräte bringen Automatisierung und Komfort in deine Sportanlage. Von Zugangskontrolle bis Lichtsteuerung laufen Abläufe zuverlässig, sicher und vollständig digital ab.',
        link: '/platzbuchungen',
        icon: faTerminal,
        variant: 'Hardware',
        highlights: ['Automatisierte Prozesse', 'Zuverlässige Technik', 'Vernetzte Anlage']
    },
    {
        title: 'Platzbuchungen',
        description:
            'Organisiere Plätze digital und transparent. Schnelle Reservierungen, klare Regeln und Übersichten in Echtzeit sorgen für eine professionelle und faire Verwaltung deiner Sportflächen.',
        link: '/platzbuchungen',
        icon: faBorderAll,
        variant: 'Digital',
        highlights: ['Echtzeit Übersicht', 'Faire Vergabe', 'Digitale Organisation'],
        width: 1
    },
    {
        title: 'Online Anmeldungen',
        description:
            'Ermögliche Anmeldungen für Kurse, Camps und Mitgliedschaften vollständig online. Papierlos, jederzeit verfügbar und mit automatischer Übernahme aller Daten in das System.',
        link: '/online-anmeldungen',
        icon: faUser,
        variant: 'Digital',
        highlights: ['Papierlos', 'Rund um die Uhr nutzbar', 'Direkter Datenimport']
    },
    {
        title: 'Trainingsplanung',
        description:
            'Übernimm Anmeldungen samt Wunschzeiten und Wunschpartnern, lass dir Trainingsgruppen automatisch vorschlagen und verteile Trainer und Plätze per Drag & Drop. Preise und Trainingszeiten gehen direkt an alle raus.',
        link: '/trainingsplanerstellung',
        icon: faChalkboardTeacher,
        variant: 'Digital',
        highlights: ['Automatische Gruppen', 'Wunschpartner & Wunschzeiten', 'Abrechnung inklusive'],
        width: 2
    },
    {
        title: 'Rechnungsstellung',
        description:
            'Automatisiere Rechnungen, Beitragsläufe und Zahlungserinnerungen. Behalte deine Finanzen jederzeit im Blick und reduziere den manuellen Aufwand durch klare digitale Abläufe.',
        link: '/rechnungen',
        icon: faFileInvoice,
        variant: 'Digital',
        highlights: ['Automatische Abrechnung', 'Transparente Finanzen', 'Schnelle Einrichtung'],
        width: 2
    },
    {
        title: 'Trainingsjournal',
        description:
            'Dokumentiere Trainingseinheiten zentral, analysiere Fortschritte und optimiere Leistungen anhand übersichtlicher Statistiken und klarer Auswertungen für Trainer und Vereine.',
        link: '/trainingsjournal',
        icon: faBook,
        variant: 'Hardware',
        highlights: ['Zentrale Dokumentation', 'Klare Auswertungen', 'Verbesserte Trainingsqualität']
    }
];

const heroExitEase = [0.22, 1, 0.36, 1] as const;
const heroExitDurationMs = 1600;
const redirectOverlayLeadInMs = 200;

export default function WelcomePage() {
    const router = useRouter();
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [showHeroDecor, setShowHeroDecor] = useState(true);
    const [showRedirectOverlay, setShowRedirectOverlay] = useState(false);
    const baseUrl = process.env.NEXT_PUBLIC_HOST;
    const canonicalUrl = baseUrl ? `${baseUrl}/` : '/';
    const orgLogoUrl = baseUrl ? `${baseUrl}/images/sportision_logo_weiss.png` : '/images/sportision_logo_weiss.png';
    const socialLinks = [
        'https://www.instagram.com/sportisionde/?hl=de',
        'https://www.youtube.com/@johannesvonsportision6701'
    ];

    const description =
        'Sportision ist die All-in-One Vereinssoftware und ein Platzbuchungssystem für Sportvereine. Verwalte Platzbuchungen, Mitglieder, Trainingsplanung und Abrechnung in einer zentralen Lösung.';

    const containerRef = useRef<HTMLDivElement>(null);
    const targetRef = useRef<HTMLDivElement>(null);
    const div1Ref = useRef<HTMLDivElement>(null);
    const div2Ref = useRef<HTMLDivElement>(null);
    const div3Ref = useRef<HTMLDivElement>(null);
    const div4Ref = useRef<HTMLDivElement>(null);
    const div5Ref = useRef<HTMLDivElement>(null);
    const div6Ref = useRef<HTMLDivElement>(null);
    const heroExitScrollFrameRef = useRef<number | null>(null);
    const lastHeroExitScrollYRef = useRef(0);
    const stableHeroExitFramesRef = useRef(0);
    const [heroExitTarget, setHeroExitTarget] = useState('/onboarding');

    const clearHeroExitScrollFrame = () => {
        if (heroExitScrollFrameRef.current === null) return;
        window.cancelAnimationFrame(heroExitScrollFrameRef.current);
        heroExitScrollFrameRef.current = null;
    };

    const finishHeroExit = () => {
        clearHeroExitScrollFrame();
        setShowHeroDecor((current) => (current ? false : current));
    };

    const waitForHeroExitScrollEnd = () => {
        const currentScrollY = window.scrollY;
        const reachedTop = currentScrollY <= 2;
        const isStable = Math.abs(currentScrollY - lastHeroExitScrollYRef.current) < 1;

        lastHeroExitScrollYRef.current = currentScrollY;
        stableHeroExitFramesRef.current = isStable ? stableHeroExitFramesRef.current + 1 : 0;

        if (reachedTop || stableHeroExitFramesRef.current >= 8) {
            finishHeroExit();
            return;
        }

        heroExitScrollFrameRef.current = window.requestAnimationFrame(waitForHeroExitScrollEnd);
    };

    const handleHeroExit = (target = '/onboarding') => {
        setHeroExitTarget(target);

        if (!showHeroDecor) {
            void router.push(target);
            return;
        }

        clearHeroExitScrollFrame();

        if (window.scrollY <= 2) {
            finishHeroExit();
            return;
        }

        lastHeroExitScrollYRef.current = window.scrollY;
        stableHeroExitFramesRef.current = 0;
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        heroExitScrollFrameRef.current = window.requestAnimationFrame(waitForHeroExitScrollEnd);
    };

    useEffect(() => {
        if (showHeroDecor) return;

        const overlayTimer = window.setTimeout(() => {
            setShowRedirectOverlay(true);
        }, heroExitDurationMs);

        const redirectTimer = window.setTimeout(() => {
            void router.push(heroExitTarget);
        }, heroExitDurationMs + redirectOverlayLeadInMs);

        return () => {
            window.clearTimeout(overlayTimer);
            window.clearTimeout(redirectTimer);
        };
    }, [heroExitTarget, router, showHeroDecor]);

    return (
        <>
            <Head>
                <title> Vereinssoftware für Buchungen, Mitglieder & Abrechnung | Sportision</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalUrl} />
                <meta
                    property="og:title"
                    content="Vereinssoftware für Buchungen, Mitglieder & Abrechnung | Sportision"
                />
                <meta property="og:description" content={description} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={canonicalUrl} />

                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'WebSite',
                            name: 'Sportision',
                            url: baseUrl ?? undefined,
                            description,
                            inLanguage: 'de',
                            potentialAction: baseUrl
                                ? {
                                      '@type': 'SearchAction',
                                      target: `${baseUrl}/search/{search_term_string}`,
                                      'query-input': 'required name=search_term_string'
                                  }
                                : undefined
                        })
                    }}
                />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: 'Sportision',
                            url: baseUrl ?? undefined,
                            logo: orgLogoUrl,
                            sameAs: socialLinks
                        })
                    }}
                />
            </Head>
            <AnimatePresence>
                {showRedirectOverlay && (
                    <motion.div
                        key="redirect-overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="tw-fixed tw-inset-0 tw-z-[99999] tw-flex tw-items-center tw-justify-center tw-bg-success"
                    />
                )}
            </AnimatePresence>
            <NavbarPages />
            {/* Background */}
            <div className="tw-absolute tw-inset-0 tw-h-screen tw-overflow-hidden tw-border-0 tw-border-b tw-border-solid tw-border-successLight tw-shadow-xl tw-shadow-success/30">
                <AnimatePresence>
                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.3}
                            width={600}
                            height={140}
                            rotate={15}
                            gradient="tw-from-success/[0.15]"
                            className="tw-left-[-10%] tw-top-[0%] -tw-z-10 md:tw-left-[-5%] md:tw-top-[20%]"
                        />
                    )}

                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.5}
                            width={500}
                            height={120}
                            rotate={15}
                            gradient="tw-from-success/[0.15]"
                            className="tw-right-[-10%] tw-top-[85%] -tw-z-10 md:tw-right-[0%] md:tw-top-[75%]"
                        />
                    )}

                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.4}
                            width={300}
                            height={80}
                            rotate={-8}
                            gradient="tw-from-success/[0.15]"
                            className="tw-bottom-[5%] tw-left-[5%] -tw-z-10 tw-hidden md:tw-bottom-[10%] md:tw-left-[10%] md:tw-block"
                        />
                    )}

                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.6}
                            width={400}
                            height={80}
                            rotate={20}
                            gradient="tw-from-success/[0.15]"
                            className="tw-right-[15%] tw-top-[10%] -tw-z-10 tw-hidden md:tw-right-[20%] md:tw-top-[15%] md:tw-block"
                        />
                    )}

                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.7}
                            width={150}
                            height={40}
                            rotate={-25}
                            gradient="tw-from-success/[0.15]"
                            className="tw-left-[20%] tw-top-[5%] -tw-z-10 tw-hidden md:tw-left-[25%] md:tw-top-[10%] md:tw-block"
                        />
                    )}

                    {showHeroDecor && (
                        <ElegantShape
                            delay={0.7}
                            width={150}
                            height={40}
                            rotate={-35}
                            gradient="tw-from-success/[0.15]"
                            className="tw-left-[70%] tw-top-[100%] -tw-z-10 tw-hidden md:tw-left-[80%] md:tw-top-[40%] md:tw-block"
                        />
                    )}
                </AnimatePresence>
            </div>
            <div className="tw-grid-cols-main tw-gap-y-16 tw-overflow-hidden lg:tw-gap-y-48">
                {/* Hero Section */}
                <section className="tw-grid-main tw-relative tw-grid tw-h-page tw-items-center md:tw-py-12">
                    <div className="tw-flex tw-h-full tw-flex-col">
                        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center md:tw-gap-20 xl:tw-gap-36">
                            {/* KI Assistent Karte ausgeblendet */}
                            {false && (
                                <div className="tw-card tw-hidden tw-min-w-[40%] tw-rounded-3xl xl:tw-block">
                                    <div className="tw-relative tw-flex tw-h-full tw-min-h-[420px] tw-flex-col tw-overflow-hidden tw-rounded-[2rem] tw-bg-gradient-to-br tw-from-white tw-via-successLight/30 tw-to-success/10 tw-p-6 md:tw-p-8">
                                        <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-bg-[radial-gradient(circle_at_top_right,_rgba(34,197,94,0.18),_transparent_42%)]" />
                                        <div className="tw-relative tw-z-10 tw-max-w-sm">
                                            <span className="tw-inline-flex tw-rounded-full tw-border tw-border-success/20 tw-bg-white/80 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-success">
                                                KI Vereinsassistent
                                            </span>
                                            <h2 className="tw-mb-3 tw-mt-4 tw-text-xl tw-font-semibold tw-leading-tight md:tw-text-2xl">
                                                Mehr Effizienz im Verein mit dem KI Assistenten von Sportision
                                            </h2>
                                            <p className="tw-mb-0 tw-max-w-md tw-text-xs tw-leading-6 tw-text-accents-7 md:tw-text-sm">
                                                Der KI Vereinsassistent automatisiert Anfragen, unterstützt Trainer und
                                                Verwaltung und liefert schnelle Antworten zu Mitgliedern,
                                                Trainingsplänen und Organisation in deinem Sportverein.
                                            </p>
                                        </div>
                                        <div className="tw-relative tw-z-10 tw-flex tw-flex-1 tw-items-center tw-justify-center">
                                            <AgenticBall
                                                className="tw-aspect-square tw-w-full tw-max-w-xs"
                                                height="auto"
                                            />
                                        </div>
                                        <Button.Link href="#ai">Jetzt entdecken</Button.Link>
                                    </div>
                                </div>
                            )}
                            <AnimatePresence initial={false}>
                                {showHeroDecor && (
                                    <motion.div
                                        key="hero-copy"
                                        className="xl:tw-max-w-auto tw-flex tw-flex-1 tw-flex-col tw-justify-center md:tw-max-w-[80%]"
                                        exit={{
                                            opacity: 0,
                                            transition: {
                                                staggerChildren: 0.08,
                                                staggerDirection: -1
                                            }
                                        }}
                                    >
                                        <motion.h1
                                            className="tw-mx-auto tw-pb-4 tw-text-center tw-text-3xl tw-font-semibold md:tw-px-0 md:tw-pb-8 md:tw-text-4xl lg:tw-text-5xl"
                                            exit={{
                                                opacity: 0,
                                                y: -110,
                                                scale: 0.82,
                                                rotate: -6,
                                                filter: 'blur(14px)',
                                                transition: {
                                                    duration: 1,
                                                    ease: heroExitEase
                                                }
                                            }}
                                        >
                                            <p className="tw-mb-0 tw-text-center tw-font-semibold tw-text-success">
                                                Vereins-
                                            </p>
                                            und
                                            <p className="tw-mb-0 tw-ml-2 tw-text-center tw-font-semibold tw-text-success">
                                                Trainingsverwaltung
                                            </p>
                                            <motion.span
                                                className="tw-inline-block"
                                                exit={{
                                                    opacity: 0,
                                                    y: -40,
                                                    scale: 0.85,
                                                    rotate: -4,
                                                    filter: 'blur(10px)',
                                                    transition: {
                                                        duration: 0.8,
                                                        ease: heroExitEase
                                                    }
                                                }}
                                            >
                                                <Highlighter
                                                    strokeWidth={2}
                                                    iterations={3}
                                                    padding={0}
                                                    action="underline"
                                                >
                                                    neu
                                                </Highlighter>
                                            </motion.span>{' '}
                                            gedacht
                                        </motion.h1>
                                        <motion.div
                                            exit={{
                                                opacity: 0,
                                                y: 36,
                                                filter: 'blur(10px)',
                                                transition: {
                                                    duration: 1,
                                                    ease: heroExitEase
                                                }
                                            }}
                                        >
                                            <motion.p
                                                className="tw-pb-4 tw-text-center tw-text-xs tw-font-bold tw-text-accents-8 md:tw-text-xl"
                                                exit={{
                                                    opacity: 0,
                                                    x: -80,
                                                    y: 20,
                                                    filter: 'blur(8px)',
                                                    transition: {
                                                        duration: 1,
                                                        ease: heroExitEase
                                                    }
                                                }}
                                            >
                                                Mit Sportision hast du alles im Blick. Von automatischer
                                                Mitgliederverwaltung und Platzbuchungen bis hin zu individuellen
                                                Trainingsplänen und Tracking.
                                            </motion.p>
                                            <div className="tw-mx-auto tw-flex tw-flex-wrap tw-items-start tw-justify-around tw-gap-4 tw-text-center *:tw-transition-all *:tw-duration-200 md:tw-justify-center md:tw-gap-20 md:tw-text-left">
                                                <motion.div
                                                    className="hover:tw-scale-110"
                                                    exit={{
                                                        opacity: 0,
                                                        x: -140,
                                                        y: 70,
                                                        rotate: -10,
                                                        filter: 'blur(10px)',
                                                        transition: {
                                                            duration: 1,
                                                            ease: heroExitEase
                                                        }
                                                    }}
                                                >
                                                    <span className="tw-block tw-text-lg tw-font-semibold md:tw-text-2xl lg:tw-text-3xl">
                                                        <CountUp
                                                            from={800}
                                                            to={1100}
                                                            separator="."
                                                            direction="up"
                                                            duration={0.1}
                                                        />
                                                        +
                                                    </span>
                                                    <span className="tw-block tw-text-xs tw-text-success md:tw-text-xl">
                                                        Institutionen
                                                    </span>
                                                </motion.div>
                                                <motion.div
                                                    className="hover:tw-scale-110"
                                                    exit={{
                                                        opacity: 0,
                                                        y: -120,
                                                        scale: 0.75,
                                                        rotate: 6,
                                                        filter: 'blur(10px)',
                                                        transition: {
                                                            duration: 1,
                                                            ease: heroExitEase
                                                        }
                                                    }}
                                                >
                                                    <span className="tw-block tw-text-lg tw-font-semibold md:tw-text-2xl lg:tw-text-3xl">
                                                        <CountUp
                                                            from={100000}
                                                            to={150000}
                                                            separator="."
                                                            direction="up"
                                                            duration={0.1}
                                                        />
                                                        +
                                                    </span>
                                                    <span className="tw-block tw-text-xs tw-text-success md:tw-text-xl">
                                                        Aktive Benutzer
                                                    </span>
                                                </motion.div>
                                                <motion.div
                                                    className="hover:tw-scale-110"
                                                    exit={{
                                                        opacity: 0,
                                                        x: 140,
                                                        y: 70,
                                                        rotate: 10,
                                                        filter: 'blur(10px)',
                                                        transition: {
                                                            duration: 1,
                                                            ease: heroExitEase
                                                        }
                                                    }}
                                                >
                                                    <span className="tw-block tw-text-lg tw-font-semibold md:tw-text-2xl lg:tw-text-3xl">
                                                        4 Jahre
                                                    </span>
                                                    <span className="tw-block tw-text-xs tw-text-success md:tw-text-xl">
                                                        eine Vision
                                                    </span>
                                                </motion.div>
                                            </div>
                                        </motion.div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <div className="tw-z-20 tw-hidden tw-items-start tw-justify-center tw-gap-2 md:tw-flex">
                            <Button.Link
                                title="Beratung anfragen"
                                size="lg"
                                href="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Beratung anfragen
                            </Button.Link>

                            <AnimatePresence>
                                {showHeroDecor && (
                                    <motion.div
                                        exit={{
                                            opacity: 0,
                                            x: 140,
                                            y: 70,
                                            rotate: 10,
                                            filter: 'blur(10px)',
                                            transition: {
                                                duration: 1,
                                                ease: heroExitEase
                                            }
                                        }}
                                    >
                                        <Button.Link flat size="lg" href="#start">
                                            Mehr erfahren
                                        </Button.Link>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        <div className="tw-z-20 tw-flex tw-items-start tw-justify-center tw-gap-2 md:tw-hidden">
                            <Button.Link
                                block
                                title="Zur Startseite"
                                href="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                            >
                                Beratung anfragen
                            </Button.Link>
                            <Button flat block icon={<FontAwesomeIcon icon={faChevronDown} />}>
                                Mehr erfahren
                            </Button>
                        </div>
                    </div>
                </section>

                {/* Beams Section */}
                <section className="tw-grid-feature" id="start">
                    <Heading>
                        Was macht Sportision{' '}
                        <Highlighter strokeWidth={2} iterations={2} action="underline" variant="success">
                            besonders?
                        </Highlighter>
                    </Heading>
                    <p className="tw-mb-8 tw-text-accents-6 md:tw-mb-24">
                        Sportision ist die zentrale Plattform für digitales Vereinsmanagement sowie moderne Software für
                        Trainingsanbieter und Sportschulen. Das intuitive System bündelt Mitgliederverwaltung,
                        Online-Buchung, Trainingsorganisation und Rechnungsabwicklung. Durch automatisierte Prozesse wie
                        digitale Anmeldungen und SEPA-Zahlungen reduziert die Lösung den Verwaltungsaufwand erheblich.
                        Sportision wurde praxisnah entwickelt und bietet Vereinen sowie professionellen Sportschulen
                        eine skalierbare Lösung zur effizienten Digitalisierung.
                    </p>
                </section>

                <section className="tw-grid-feature" id="modules">
                    <div
                        ref={containerRef}
                        className="tw-relative tw-flex tw-w-full tw-items-center tw-justify-center tw-overflow-hidden"
                    >
                        <div className="tw-pointer-events-none tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-between md:tw-flex-row">
                            <div className="tw-pointer-events-auto tw-flex tw-w-full tw-flex-col tw-gap-4 tw-overflow-visible md:tw-w-auto md:tw-self-center md:tw-text-right lg:tw-gap-12">
                                <PlaceholderBlock
                                    title="Mitgliederverwaltung"
                                    description="Mitglieder übersichtlich verwalten, klar strukturiert und automatisiert."
                                    href="/mitgliederverwaltung"
                                    ref={div1Ref}
                                />
                                <PlaceholderBlock
                                    title="Platzbuchungen"
                                    description="Plätze digital verwalten, schnelle Buchung, klare Belegung in Echtzeit."
                                    href="/platzbuchungen"
                                    ref={div2Ref}
                                />

                                <PlaceholderBlock
                                    title="Rechnungsstellung"
                                    description="Rechnungen automatisieren, Zahlungen verwalten, Finanzen im Blick."
                                    href="/rechnungen"
                                    ref={div6Ref}
                                />
                            </div>

                            <div className="tw-flex tw-flex-1 tw-justify-center tw-py-8">
                                <div
                                    ref={targetRef}
                                    className="tw-flex tw-size-[140px] tw-items-center tw-justify-center tw-rounded-full tw-bg-white tw-shadow-xl"
                                >
                                    <LogoLoader width={120} staticColor="#38c172" />
                                </div>
                            </div>

                            <div className="tw-pointer-events-auto tw-flex tw-w-full tw-flex-col tw-gap-4 tw-overflow-visible md:tw-w-auto md:tw-self-center lg:tw-gap-12">
                                <PlaceholderBlock
                                    title="Trainingspläne"
                                    description="Trainingspläne erstellen, teilen und Fortschritte effizient verfolgen."
                                    href="/trainingsplanerstellung"
                                    ref={div4Ref}
                                />

                                <PlaceholderBlock
                                    title="Trainingsjournal"
                                    description="Training erfassen, Fortschritt analysieren und Leistung optimieren."
                                    href="/trainingsjournal"
                                    ref={div5Ref}
                                />

                                <PlaceholderBlock
                                    title="Online-Anmeldung"
                                    description="Training festhalten, Fortschritt analysieren, Leistung optimieren."
                                    href="/online-anmeldung"
                                    ref={div3Ref}
                                />
                            </div>
                        </div>

                        {/* Animated Beams - Always Visible (responsive coords) */}
                        <div className="tw-block" aria-hidden="true">
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div1Ref}
                                toRef={targetRef}
                                curvature={30}
                            />
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div2Ref}
                                toRef={targetRef}
                                curvature={isMobile ? -80 : 0}
                            />
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div3Ref}
                                toRef={targetRef}
                                curvature={-30}
                                reverse
                            />
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div4Ref}
                                toRef={targetRef}
                                curvature={30}
                                reverse
                            />
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div5Ref}
                                toRef={targetRef}
                                curvature={isMobile ? 80 : 0}
                                reverse
                            />
                            <AnimatedBeam
                                containerRef={containerRef}
                                fromRef={div6Ref}
                                toRef={targetRef}
                                curvature={-30}
                            />
                        </div>
                    </div>
                </section>
                <AiCmsShowcaseSection />

                {/* References */}
                <section className="tw-grid-feature">
                    <Heading>
                        <Highlighter
                            strokeWidth={3}
                            iterations={5}
                            action="bracket"
                            variant="success"
                            brackets={['left', 'right']}
                        >
                            Referenzen
                        </Highlighter>{' '}
                    </Heading>
                    <p className="tw-mb-8 tw-text-accents-6 md:tw-mb-24">
                        Vereine und Sportschulen mit glücklichen Mitgliedern und Kunden.{' '}
                        <Link
                            title="Zur Startseite"
                            href="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                            className="tw-text-success hover:tw-text-success/70"
                        >
                            Möchtest du auch dazu gehören?
                        </Link>
                    </p>
                    <div className="tw-relative tw-w-full tw-overflow-hidden tw-py-4">
                        <LogoCarousel />
                    </div>
                </section>
                {/* Vereinsverwaltung */}
                {/* Vereinsverwaltung Section */}
                <section className="tw-grid-feature">
                    <FeatureRow
                        header={
                            <>
                                <h2>
                                    Vereinsverwaltung{' '}
                                    <Highlighter variant="successLight" iterations={2}>
                                        {' '}
                                        sicher, einfach und zukunftsorientiert
                                    </Highlighter>
                                </h2>

                                <p className="tw-mb-8 tw-text-accents-6 md:tw-mb-24">
                                    Sportision schafft durch zentrale, sichere Datenerfassung und moderne Prozesse eine
                                    effiziente, professionelle Grundlage für die digitale Zukunft deines Vereins.
                                </p>
                            </>
                        }
                        imageSrc="/images/modules/club_members.png"
                        imageAlt="Vereinsverwaltung Dashboard"
                        ctaLink="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                        ctaText="Vereinsalltag erleichtern"
                    >
                        <p className="tw-text-lg tw-leading-relaxed">
                            Sportision macht die Vereinsverwaltung übersichtlich und effizient. Alle Daten werden
                            zentral sowie sicher erfasst. Moderne Prozesse reduzieren den Aufwand und schaffen eine
                            professionelle digitale Grundlage für die Zukunft deines Vereins.
                        </p>

                        <ul className="tw-flex tw-flex-col tw-gap-3 tw-p-0">
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Automatisierung:</strong> Spare Zeit und Ressourcen bei Routineaufgaben.
                                </span>
                            </li>
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Flexibilität:</strong> Anpassbar für kleine Vereine und große Sportclubs.
                                </span>
                            </li>
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Kontrolle:</strong> Jederzeit Überblick über Finanzen und Abläufe.
                                </span>
                            </li>
                        </ul>
                    </FeatureRow>
                </section>

                {/* Trainingsverwaltung Section (Reversed Layout) */}
                <section className="tw-grid-feature">
                    <FeatureRow
                        reverse={true}
                        header={
                            <>
                                <h2>
                                    Trainingsverwaltung{' '}
                                    <Highlighter action="underline" strokeWidth={2} iterations={2}>
                                        präzise, effizient und benutzerfreundlich{' '}
                                    </Highlighter>
                                </h2>
                                <p className="tw-mb-8 tw-text-accents-6 md:tw-mb-24">
                                    Mit einem klaren, benutzerfreundlichen Aufbau ermöglicht Sportision eine
                                    Trainingsverwaltung, die Abläufe vereinfacht, wertvolle Zeit spart und eine
                                    professionelle Grundlage für nachhaltige sportliche Entwicklung schafft.
                                </p>
                            </>
                        }
                        imageSrc="/images/modules/journal.png"
                        imageAlt="Trainingsverwaltung Tablet View"
                        ctaLink="https://calendly.com/sportision-team/beratung-zur-digitalisierung-mit-sportision"
                        ctaText="Jetzt starten"
                    >
                        <p className="tw-text-lg tw-leading-relaxed">
                            Hier ist die gekürzte Fassung: Sportision optimiert die Trainingsorganisation durch
                            strukturierte Planung, einfache Teilnehmerzuweisung und digitale Anwesenheitserfassung.
                            Durch die transparente Dokumentation von Fortschritten und Inhalten behalten Trainer stets
                            den Überblick und steigern nachhaltig die Trainingsqualität.
                        </p>

                        <ul className="tw-flex tw-flex-col tw-gap-3 tw-p-0">
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Planung:</strong> Erstelle individuelle Trainingspläne per Drag & Drop.
                                </span>
                            </li>
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Analyse:</strong> Detaillierte Statistiken zur Leistungssteigerung.
                                </span>
                            </li>
                            <li className="tw-flex tw-items-start tw-gap-3">
                                <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                <span>
                                    <strong>Kommunikation:</strong> Direktes Feedback zwischen Trainer und Sportler.
                                </span>
                            </li>
                        </ul>
                    </FeatureRow>
                </section>

                {/* Testimonials */}
                {/* <section className="tw-relative tw-grid tw-grid-cols-main">
                    <Heading>
                        <Highlighter strokeWidth={3} padding={4} action="box">
                            Unsere Ideengeber
                        </Highlighter>{' '}
                    </Heading>
                    <p className="tw-mb-6 tw-text-accents-6">
                        Wir arbeiten eng mit Vereinen und Sportschulen zusammen, um Sportision kontinuierlich zu
                        verbessern. Dabei legen wir großen Wert auf das Feedback unserer Nutzer.
                    </p>
                    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-h-12 tw-bg-gradient-to-b tw-from-transparent tw-to-white" />
                    <div className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-10 tw-h-12 tw-bg-gradient-to-t tw-from-transparent tw-to-white" />
                    <AnimatedTestimonials testimonials={testimonials} />
                    </section> */}

                <section className="tw-grid-feature tw-from-success/6 tw-to-success/12 tw-relative tw-overflow-hidden tw-rounded-[2rem] tw-bg-gradient-to-br tw-via-white tw-p-6 md:tw-p-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        viewport={{ once: true, margin: '-100px' }}
                        className="tw-relative"
                    >
                        <div className="tw-mb-4 tw-inline-flex tw-items-center tw-rounded-full tw-bg-success/10 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.18em] tw-text-success">
                            Starte hier
                        </div>
                        <Heading className="tw-mb-4">
                            <Highlighter strokeWidth={2} iterations={2} action="highlight" variant="successLight">
                                Onboarding für Vereine und Trainingsschulen
                            </Highlighter>
                        </Heading>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
                        viewport={{ once: true, margin: '-100px' }}
                        className="tw-mb-8 tw-max-w-3xl tw-text-accents-6 md:tw-mb-12"
                    >
                        Starte mit Sportision in dem Setup, das zu deiner Organisation passt. Ob Vereinssoftware für
                        Mitgliederverwaltung, Buchungen und Abrechnung oder digitale Lösung für Trainingsschulen mit
                        professioneller Kurs- und Trainingsplanung: Der passende Onboarding-Weg führt dich direkt zur
                        richtigen Einrichtung.
                    </motion.p>

                    <div className="tw-grid tw-grid-cols-1 tw-gap-6 lg:tw-grid-cols-2">
                        <motion.article
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                            viewport={{ once: true, margin: '-100px' }}
                            className="tw-card-bordered tw-relative tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-white tw-via-white tw-to-success/10 tw-p-6 tw-ring-1 tw-ring-success/10 md:tw-p-8"
                        >
                            <div className="tw-absolute tw-right-0 tw-top-0 tw-h-32 tw-w-32 tw-rounded-full tw-bg-success/10 tw-blur-3xl" />
                            <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-gap-5">
                                <div className="tw-flex tw-items-start tw-gap-3">
                                    <div className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-success/10 tw-text-success">
                                        <FontAwesomeIcon icon={faUsers} className="tw-text-xl" />
                                    </div>
                                    <div>
                                        <p className="tw-mb-1 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-success">
                                            Für Vereine
                                        </p>
                                        <h3 className="tw-mb-0 tw-text-2xl tw-font-semibold tw-text-accents-9">
                                            Vereinssoftware schnell passend einrichten
                                        </h3>
                                    </div>
                                </div>

                                <p className="tw-mb-0 tw-text-base tw-leading-relaxed tw-text-accents-7">
                                    Ideal für Sportvereine, Abteilungen und Clubs, die Mitglieder digital verwalten,
                                    Trainings organisieren und Verwaltungsaufwand reduzieren möchten.
                                </p>

                                <ul className="tw-mb-0 tw-flex tw-flex-col tw-gap-3 tw-p-0 tw-text-accents-7">
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>
                                            Mitgliederverwaltung, Buchungen und Beitragsprozesse sauber aufsetzen.
                                        </span>
                                    </li>
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>
                                            Passende Module für Vereinsalltag, Trainer und Mitglieder auswählen.
                                        </span>
                                    </li>
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>
                                            Den Einstieg für kleine Vereine ebenso wie wachsende Clubs vorbereiten.
                                        </span>
                                    </li>
                                </ul>

                                <Button
                                    size="lg"
                                    className="tw-mt-auto"
                                    onPress={() => handleHeroExit('/onboarding?organizationType=verein')}
                                >
                                    Onboarding für Vereine
                                </Button>
                            </div>
                        </motion.article>

                        <motion.article
                            initial={{ opacity: 0, x: 30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
                            viewport={{ once: true, margin: '-100px' }}
                            className="tw-card-bordered tw-relative tw-overflow-hidden tw-rounded-3xl tw-bg-gradient-to-br tw-from-white tw-via-white tw-to-success/10 tw-p-6 tw-ring-1 tw-ring-success/10 md:tw-p-8"
                        >
                            <div className="tw-absolute tw-right-0 tw-top-0 tw-h-32 tw-w-32 tw-rounded-full tw-bg-success/10 tw-blur-3xl" />
                            <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-gap-5">
                                <div className="tw-flex tw-items-start tw-gap-3">
                                    <div className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-2xl tw-bg-success/10 tw-text-success">
                                        <FontAwesomeIcon icon={faChalkboardTeacher} className="tw-text-xl" />
                                    </div>
                                    <div>
                                        <p className="tw-mb-1 tw-text-sm tw-font-semibold tw-uppercase tw-tracking-[0.2em] tw-text-success">
                                            Für Trainingsschulen
                                        </p>
                                        <h3 className="tw-mb-0 tw-text-2xl tw-font-semibold tw-text-accents-9">
                                            Digitale Abläufe für professionelle Trainingsangebote
                                        </h3>
                                    </div>
                                </div>

                                <p className="tw-mb-0 tw-text-base tw-leading-relaxed tw-text-accents-7">
                                    Für Trainingsschulen, Akademien und Leistungszentren, die Teilnehmer, Kurse,
                                    Trainerteams und wiederkehrende Abläufe in einer zentralen Plattform bündeln wollen.
                                </p>

                                <ul className="tw-mb-0 tw-flex tw-flex-col tw-gap-3 tw-p-0 tw-text-accents-7">
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>Kursangebote, Trainingsgruppen und Kapazitäten strukturiert planen.</span>
                                    </li>
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>
                                            Abrechnung, Kommunikation und Organisation für wachsende Angebote
                                            vorbereiten.
                                        </span>
                                    </li>
                                    <li className="tw-flex tw-items-start tw-gap-3">
                                        <FontAwesomeIcon icon={faCheckCircle} className="tw-mt-1 tw-text-success" />
                                        <span>
                                            Den Setup-Prozess direkt auf Trainingsschulen und Sportakademien abstimmen.
                                        </span>
                                    </li>
                                </ul>

                                <Button
                                    size="lg"
                                    className="tw-mt-auto"
                                    onPress={() => handleHeroExit('/onboarding?organizationType=trainingsschule')}
                                >
                                    Onboarding für Trainingsschulen
                                </Button>
                            </div>
                        </motion.article>
                    </div>
                </section>
                {/* Module */}
                <section className="tw-grid-feature">
                    <Heading>
                        <Highlighter strokeWidth={2} iterations={3} brackets={['top', 'bottom']} action="bracket">
                            {' '}
                            Unsere Module in der Übersicht
                        </Highlighter>
                    </Heading>
                    <p className="tw-mb-8 tw-text-accents-6 md:tw-mb-24">
                        Sportision bündelt alle zentralen Funktionen für Vereins- und Trainingsmanagement in einem
                        übersichtlichen System. Jedes Modul ist darauf ausgelegt, Abläufe zu vereinfachen, Transparenz
                        zu schaffen und deinen Alltag effizienter zu gestalten.
                    </p>
                    <div className="tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-3">
                        <ModulesGrid modules={modules} />
                    </div>
                </section>

                <PriceSection className="tw-grid-feature" badge="Kontaktiere uns jetzt!"></PriceSection>
                <Chatbot />
            </div>
            <Footer />
        </>
    );
}

interface PlaceholderBlockProps {
    title?: string;
    description?: string;
    className?: string;
    children?: ReactNode;
    href?: string;
}

const PlaceholderBlock = forwardRef<HTMLDivElement, PlaceholderBlockProps>(
    ({ title, description, href, className }, ref) => {
        return (
            <div
                ref={ref}
                className={
                    'tw-card-bordered tw-card-clickable tw-group tw-pointer-events-auto tw-flex tw-w-full tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-transition-all md:tw-w-fit md:tw-max-w-[200px] lg:tw-max-w-[300px]' +
                    (className ?? '')
                }
            >
                <Link href={href ?? '/'}>
                    <p className="tw-text-md tw-mb-2 tw-text-accents-9 tw-transition-all group-hover:tw-text-success md:tw-text-lg lg:tw-text-2xl">
                        {title}
                    </p>
                    <p className="tw-mb-0 tw-text-[10px] tw-text-accents-7 md:tw-text-xs lg:tw-text-sm">
                        {' '}
                        {description}
                    </p>
                </Link>
            </div>
        );
    }
);

PlaceholderBlock.displayName = 'PlaceholderBlock';

function Heading({ children, className }: { children: ReactNode; className?: string }) {
    return <h2 className={cn('tw-text-2xl tw-font-semibold lg:tw-text-3xl', className)}>{children}</h2>;
}
